import axios from 'axios';

// ---------------------------------------------------------------------------
// Runtime-switchable backend URL
//
// Priority order (highest → lowest):
//   1. localStorage key 'BACKEND_URL' (set from Settings page at runtime)
//   2. Auto-fallback: localhost:5000 when running on localhost,
//      current LAN host:5000 when opened from a local Wi-Fi link,
//   3. REACT_APP_API_URL build-time env var
//   4. Render URL when running on Vercel / any other domain
//
// Changing the localStorage value and reloading the page switches the backend.
// ---------------------------------------------------------------------------

const LOCALSTORAGE_KEY = 'BACKEND_URL';
const RENDER_URL       = 'https://objectdetection-6gnn.onrender.com';

function buildBaseUrl() {
  // 1. Runtime override (Settings page)
  try {
    const saved = localStorage.getItem(LOCALSTORAGE_KEY);
    if (saved && saved.trim()) return normalizeUrl(saved.trim());
  } catch (_) { /* localStorage blocked in some browsers */ }

  // 2. Auto-detect local/LAN first so phones opened through a Wi-Fi link do
  // not accidentally call Render for private camera streams.
  const { protocol, hostname } = window.location;
  const onLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  if (onLocalhost) return 'http://localhost:5000/api';

  if (isPrivateLanHost(hostname)) {
    return `${protocol}//${hostname}:5000/api`;
  }

  // 3. Build-time env var
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl && envUrl.trim()) return normalizeUrl(envUrl.trim());

  // 4. Cloud fallback
  return `${RENDER_URL}/api`;
}

function normalizeUrl(raw) {
  const trimmed = raw.replace(/\/+$/, '');
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
}

function isPrivateLanHost(hostname) {
  if (!hostname) return false;
  if (hostname.endsWith('.local')) return true;

  const parts = hostname.split('.').map(part => Number(part));
  if (parts.length !== 4 || parts.some(part => !Number.isInteger(part) || part < 0 || part > 255)) {
    return false;
  }

  const [a, b] = parts;
  return (
    a === 10 ||
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 168)
  );
}

// Live reference — re-read each call so runtime changes take effect
export const getApiBaseUrl = () => buildBaseUrl();

// Helper for the Settings page — reads/writes localStorage
export const RENDER_BACKEND_URL = RENDER_URL;
export const getStoredBackendUrl = () => {
  try { return localStorage.getItem(LOCALSTORAGE_KEY) || ''; } catch (_) { return ''; }
};
export const setStoredBackendUrl = (url) => {
  try {
    if (!url || !url.trim()) localStorage.removeItem(LOCALSTORAGE_KEY);
    else localStorage.setItem(LOCALSTORAGE_KEY, url.trim());
  } catch (_) { /* ignore */ }
};

// ---------------------------------------------------------------------------
// Axios — re-create client per request so URL changes are picked up
// ---------------------------------------------------------------------------
const IMAGE_UPLOAD_TIMEOUT_MS = Number(process.env.REACT_APP_IMAGE_UPLOAD_TIMEOUT_MS || 180000);
const VIDEO_UPLOAD_TIMEOUT_MS = Number(process.env.REACT_APP_VIDEO_UPLOAD_TIMEOUT_MS || 240000);
const API_REQUEST_TIMEOUT_MS  = Number(process.env.REACT_APP_API_TIMEOUT_MS || 90000);

function getClient() {
  return axios.create({ baseURL: buildBaseUrl(), timeout: API_REQUEST_TIMEOUT_MS });
}

// ---------------------------------------------------------------------------
// Logging
// ---------------------------------------------------------------------------
function logOk(label, detail)  { console.info(`[api] ${label}`, detail); }
function logErr(label, err, extra = {}) {
  console.error(`[api] ${label}`, {
    baseUrl: buildBaseUrl(), message: err.message, code: err.code,
    status: err.response?.status, data: err.response?.data,
    hasRequest: Boolean(err.request), ...extra,
  });
}

// ---------------------------------------------------------------------------
// Error formatter — converts axios errors to user-readable strings
// ---------------------------------------------------------------------------
export function formatDetectionError(err) {
  if (err.response) {
    const { status, data } = err.response;
    if (status === 503) {
      return (data?.detail || data?.error) ||
        'The backend is still warming up. Please wait ~30 seconds and try again.';
    }
    if (data?.error) return data.hint ? `${data.error} — ${data.hint}` : data.error;
    return `Server error ${status}: ${JSON.stringify(data)}`;
  }
  if (err.request) {
    if (err.code === 'ECONNABORTED' || err.message?.toLowerCase().includes('timeout')) {
      return 'Request timed out. The backend may be cold-starting — please wait ~30 seconds and try again.';
    }
    const base = buildBaseUrl();
    const isLocal =
      base.includes('localhost') ||
      base.includes('127.0.0.1') ||
      isPrivateLanHost(new URL(base).hostname);
    return isLocal
      ? `Cannot reach local backend at ${base}. Make sure Docker is running and port 5000 is reachable from this device.`
      : `No response from ${base}. The Render backend may be sleeping — wait 30s and retry.`;
  }
  return err.message || 'An unexpected error occurred.';
}

// ---------------------------------------------------------------------------
// Detection
// ---------------------------------------------------------------------------

/**
 * IMPORTANT: Do NOT set 'Content-Type' manually for FormData requests.
 * The browser auto-adds the multipart boundary; setting it manually removes the
 * boundary and causes the server to drop the connection ("no response").
 */
export const uploadImage = async (file, confidence = 0.5, selectedObjects = []) => {
  const base = buildBaseUrl();
  const endpoint = `${base}/detection/image`;
  const formData = new FormData();
  formData.append('image', file);
  formData.append('confidence', String(confidence));
  formData.append('selected_objects', JSON.stringify(selectedObjects));

  logOk('uploadImage →', { endpoint, fileName: file?.name, fileSize: file?.size });
  try {
    const res = await axios.post(endpoint, formData, { timeout: IMAGE_UPLOAD_TIMEOUT_MS });
    logOk('uploadImage ←', { status: res.status, count: res.data?.count });
    return res.data;
  } catch (err) {
    logErr('uploadImage ✗', err, { endpoint });
    throw err;
  }
};

export const uploadVideo = async (file, confidence = 0.5, maxFrames = 30, selectedObjects = []) => {
  const formData = new FormData();
  formData.append('video', file);
  formData.append('confidence', String(confidence));
  formData.append('max_frames', String(maxFrames));
  formData.append('selected_objects', JSON.stringify(selectedObjects));
  const res = await axios.post(`${buildBaseUrl()}/detection/video`, formData, {
    timeout: VIDEO_UPLOAD_TIMEOUT_MS,
  });
  return res.data;
};

// ---------------------------------------------------------------------------
// Video / Stream (server-side — RTSP needs local Docker backend)
// ---------------------------------------------------------------------------
export const startStream = async (streamId, source, selectedObjects = [], opts = {}) => {
  const res = await getClient().post('/video/start-stream', {
    stream_id: streamId, source, selected_objects: selectedObjects, ...opts,
  });
  return res.data;
};
export const stopStream   = async (id)  => (await getClient().post(`/video/stop-stream/${id}`)).data;
export const getFrame     = async (id)  => (await getClient().get(`/video/get-frame/${id}`)).data;
export const discoverCameras = async (opts = {}) => (await getClient().post('/video/cameras/discover', opts)).data;
export const getSavedCameras = async ()          => (await getClient().get('/video/cameras')).data;
export const saveCamera      = async (cam)       => (await getClient().post('/video/cameras', cam)).data;

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------
export const getHealth = async () =>
  (await getClient().get('/analytics/health', { timeout: 15000 })).data;

export const analyzeDetections = async (detections, context = '') =>
  (await getClient().post('/analytics/analyze', { detections, context })).data;

export const analyzeSuspiciousActivity = async (logs, context = '') =>
  (await getClient().post('/analytics/suspicious-activity', { logs, context })).data;

export const generateAlert = async (type, data) =>
  (await getClient().post('/analytics/alert', { type, data })).data;

// Backwards-compatible
export const handleApiError = (err) => {
  if (err.response) return err.response.data?.error || 'An error occurred';
  if (err.request)  return 'No response from server';
  return err.message;
};
